import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/**
 * Distinct crosstable-header abbreviation per team, in the same order as [names]. Starts from each
 * word's initial (short for multi-word club names), then escalates how many letters the FIRST word
 * contributes -- for whichever teams still collide -- until the whole set is unique. This handles
 * both multi-word collisions (e.g. "Sokol Bowling"/"STS Brod" both -> "SB" at 1 letter) and
 * single-word team names (e.g. "Kocouři"/"Kopyta"/"Káčka" all -> "K" at 1 letter, common in this
 * league's real data) with one mechanism. Falls back to a numeric suffix for the pathological case
 * of two literally-identical names.
 */
private fun uniqueAbbreviations(names: List<String>): List<String> {
    fun candidate(name: String, firstWordLen: Int): String {
        val words = name.split(Regex("\\s+")).filter { it.isNotBlank() }
        if (words.isEmpty()) return ""
        val restInitials = words.drop(1).mapNotNull { it.firstOrNull() }
        return (words[0].take(firstWordLen) + restInitials.joinToString("")).uppercase()
    }

    val lens = IntArray(names.size) { 1 }
    var current = names.indices.map { candidate(names[it], lens[it]) }
    var round = 0
    while (round < 8) {
        val counts = current.groupingBy { it }.eachCount()
        val colliding = current.indices.filter { counts.getValue(current[it]) > 1 }
        if (colliding.isEmpty()) break
        colliding.forEach { lens[it]++ }
        current = names.indices.map { candidate(names[it], lens[it]) }
        round++
    }

    val used = mutableSetOf<String>()
    return current.map { base ->
        var v = base
        var n = 2
        while (!used.add(v)) v = "$base${n++}"
        v
    }
}

private fun medalClass(rank: Int): String? = when (rank) {
    1 -> "gold"
    2 -> "silver"
    3 -> "bronze"
    else -> null
}

/** Rounds to 1 decimal and always shows it (150.0, not 150) -- Double.toString() drops a trailing ".0". */
fun formatAvg(value: Double): String = value.asDynamic().toFixed(1) as String

private fun roundOrderKey(m: MatchDto): Int = (if (m.phase == "autumn") 0 else 1) * 1000 + m.roundNumber

/** Human-readable explanation of which spec 5.3 criterion decided a tied ranking, or null if no tie-break applied. */
private fun tieBreakDescription(chip: String?): String? = when (chip) {
    "H2H_POINTS" -> "Při shodě bodů rozhodl výsledek vzájemných zápasů (kdo je vyhrál)."
    "H2H_DELTA" -> "Při shodě bodů i výsledku vzájemných zápasů rozhodl rozdíl duelových bodů ve vzájemných zápasech."
    "H2H_SUM" -> "Při shodě předchozích kritérií rozhodl součet kuželek ve vzájemných zápasech."
    "AVG" -> "Při shodě předchozích kritérií rozhodl průměr kuželek za všechna utkání."
    "MINI_TABLE" -> "Tři a více týmů měly shodu bodů — pořadí určila mini-tabulka jejich vzájemných zápasů."
    "SEED" -> "Týmy zůstaly zcela vyrovnané — pořadí rozhodlo nasazením v této lize."
    else -> null
}

private fun computeDuelPointsByTeam(matches: List<MatchDto>): Map<String, Pair<Int, Int>> {
    val result = mutableMapOf<String, Pair<Int, Int>>()
    matches.filter { it.status == "completed" }.forEach { m ->
        val home = m.homeDuelPoints ?: 0
        val away = m.awayDuelPoints ?: 0
        val homeSoFar = result[m.homeTeamId] ?: (0 to 0)
        result[m.homeTeamId] = (homeSoFar.first + home) to (homeSoFar.second + away)
        val awaySoFar = result[m.awayTeamId] ?: (0 to 0)
        result[m.awayTeamId] = (awaySoFar.first + away) to (awaySoFar.second + home)
    }
    return result
}

/** Shared "Tabulka ligy" layout, reused by both the live and phase-final ("Finální tabulka") tables. */
@Composable
private fun StandingsTable(
    standings: List<StandingsRowDto>,
    duelPointsByTeam: Map<String, Pair<Int, Int>>,
    playoffGroupByTeam: Map<String, String?> = emptyMap(),
    showMedals: Boolean = true,
) {
    // Nadstavba (spec 12): O titul and O udržení are separate races, not one combined ranking -- O
    // titul teams always stay above O udržení regardless of raw point total once split. Group first
    // (stable sort keeps each group's own relative order), then renumber sequentially so the "#"
    // column has no gaps/duplicates.
    val orderedRows = if (playoffGroupByTeam.isEmpty()) {
        standings
    } else {
        standings
            .sortedBy { if (playoffGroupByTeam[it.teamId] == "relegation") 1 else 0 }
            .mapIndexed { index, row -> row.copy(rank = index + 1) }
    }

    Table(attrs = { classes("sticky-2") }) {
        Tr {
            Th { Text("#") }
            Th { Text("Tým") }
            Th { Text("Z") }
            Th { Text("V") }
            Th { Text("R") }
            Th { Text("P") }
            Th(attrs = { classes("text-center") }) { Text("Duelové body") }
            Th { Text("Průměr") }
            Th(attrs = { classes("col-accent") }) { Text("Body") }
        }
        orderedRows.forEachIndexed { index, row ->
            val totalLosses = row.losses + row.forfeitLosses
            val (duelWon, duelLost) = duelPointsByTeam[row.teamId] ?: (0 to 0)
            val avg = if (row.gamesPlayed > 0) row.totalScoreFor.toDouble() / row.gamesPlayed else 0.0
            val tieBreakDesc = tieBreakDescription(row.tieBreakChip)
            val isGroupBoundary = index > 0 &&
                playoffGroupByTeam[row.teamId] != null &&
                playoffGroupByTeam[orderedRows[index - 1].teamId] != playoffGroupByTeam[row.teamId]
            Tr(attrs = { if (isGroupBoundary) classes("group-boundary") }) {
                Td(attrs = { tieBreakDesc?.let { attr("title", it) } }) { RankBadge(row.rank, showMedals) }
                Td { Text(row.teamName) }
                Td { Text(row.played.toString()) }
                Td { Text(row.wins.toString()) }
                Td { Text(row.draws.toString()) }
                Td { Text(if (row.forfeitLosses > 0) "$totalLosses (K:${row.forfeitLosses})" else "$totalLosses") }
                Td(attrs = { classes("text-center") }) { Text("$duelWon:$duelLost") }
                Td { Text(formatAvg(avg)) }
                Td(attrs = { tieBreakDesc?.let { attr("title", it) } }) {
                    Span(attrs = { classes("pill") }) {
                        Text(row.points.toString())
                    }
                }
            }
        }
    }
}

/** H2 with a clickable "#" permalink to the section's own id (the whole card, not just its content). */
@Composable
private fun SectionHeading(title: String, anchorId: String) {
    H2 {
        Text(title)
        A(href = "#$anchorId", attrs = { classes("anchor-link"); attr("title", "Odkaz na tuto sekci") }) { Text("#") }
    }
}

@Composable
fun RankBadge(rank: Int, showMedals: Boolean = true, prizePlaces: Int = 3) {
    val highlight = if (showMedals) {
        medalClass(rank) ?: "prize".takeIf { rank in 4..prizePlaces }
    } else null
    Span(attrs = {
        classes(*listOfNotNull("rank-badge", highlight).toTypedArray())
        if (showMedals && prizePlaces > 3 && rank in 1..prizePlaces) attr("title", "Oceňované místo")
    }) {
        Text(rank.toString())
    }
}

/** Standings scoped to one already-finished half of the season (spec 5.4) — cached per phase. */
@Composable
private fun PhaseFinalStandings(
    leagueId: String,
    phase: String,
    phaseMatches: List<MatchDto>,
    title: String,
    anchorId: String,
    showMedals: Boolean = true,
) {
    var standings by remember(phase) { mutableStateOf<List<StandingsRowDto>>(emptyList()) }
    var loading by remember(phase) { mutableStateOf(true) }
    var error by remember(phase) { mutableStateOf<String?>(null) }
    val duelPointsByTeam = remember(phaseMatches) { computeDuelPointsByTeam(phaseMatches) }

    LaunchedEffect(leagueId, phase) {
        loading = true
        try {
            standings = Api.fetchStandings(null, leagueId, phase).standings
            error = null
        } catch (e: Throwable) {
            error = "Načtení finální tabulky selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    Div(attrs = { classes("card"); attr("id", anchorId) }) {
        SectionHeading(title, anchorId)
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }
        StandingsTable(standings, duelPointsByTeam, showMedals = showMedals)
    }
}

@Composable
fun PublicLeagueScreen(leagueId: String, leagueName: String) {
    var standings by remember { mutableStateOf<List<StandingsRowDto>>(emptyList()) }
    var teams by remember { mutableStateOf<List<SeasonLeagueTeamDto>>(emptyList()) }
    var matches by remember { mutableStateOf<List<MatchDto>>(emptyList()) }
    var playerStats by remember { mutableStateOf<List<PlayerStatDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedMatch by remember { mutableStateOf<MatchDto?>(null) }

    LaunchedEffect(leagueId) {
        loading = true
        try {
            standings = Api.fetchStandings(null, leagueId).standings
            teams = Api.fetchLeagueTeams(null, leagueId)
            matches = Api.fetchMatches(null, leagueId)
            playerStats = Api.fetchPlayerStats(null, leagueId = leagueId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení ligy selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    // Deep-linking into a section, e.g. /3liga#vzajemne-zapasy — scroll once the section exists.
    LaunchedEffect(loading) {
        if (!loading) {
            val hash = window.location.hash.removePrefix("#")
            if (hash.isNotEmpty()) {
                document.getElementById(hash)?.scrollIntoView()
            }
        }
    }

    val rankByTeamId = remember(standings) { standings.associate { it.teamId to it.rank } }
    val orderedTeams = remember(teams, rankByTeamId) {
        teams.sortedBy { rankByTeamId[it.teamId] ?: it.seed ?: Int.MAX_VALUE }
    }
    val teamNameById = remember(teams) { teams.associate { it.teamId to (it.teamNameOverride ?: it.team?.name ?: it.teamId) } }
    val crossTableAbbrevById = remember(orderedTeams) {
        orderedTeams.map { it.teamId }.zip(uniqueAbbreviations(orderedTeams.map { it.teamNameOverride ?: it.team?.name ?: "" })).toMap()
    }
    // Nadstavba (spec 12): scoped to autumn+spring only -- the crosstable's direct/reverse cells
    // assume exactly one match per ordered team pair per leg, which a group's single extra playoff
    // match (a 3rd meeting between the same two teams) would otherwise silently overwrite.
    val baseMatches = remember(matches) { matches.filter { it.phase == "autumn" || it.phase == "spring" } }
    val matchByPair = remember(baseMatches) { baseMatches.associateBy { it.homeTeamId to it.awayTeamId } }
    // Nadstavba (spec 12): the extra match is single-leg (no fixed home/away convention to rely on
    // like the base part's direct/reverse), so it's keyed by the unordered team pair instead.
    val playoffMatchByPair = remember(matches) {
        matches
            .filter { (it.phase == "playoff_title" || it.phase == "playoff_relegation") && it.status == "completed" }
            .associateBy { setOf(it.homeTeamId, it.awayTeamId) }
    }
    val duelPointsByTeam = remember(matches) { computeDuelPointsByTeam(matches) }
    // Nadstavba (spec 12): once split, teams stay in this single combined table (points already carry
    // over automatically) -- just badge each row with its group so the split is visible before the
    // group's own final table appears (see gating below).
    val playoffGroupByTeam = remember(teams) { teams.associate { it.teamId to it.playoffGroup } }

    if (loading) Text("Načítám…")
    error?.let { Div(attrs = { classes("card") }) { Text("Chyba: $it") } }

    Div(attrs = { classes("card"); attr("id", "tabulka-ligy") }) {
        SectionHeading("Tabulka ligy", "tabulka-ligy")
        StandingsTable(standings, duelPointsByTeam, playoffGroupByTeam = playoffGroupByTeam)
    }

    Div(attrs = { classes("card"); attr("id", "vzajemne-zapasy") }) {
        SectionHeading("Vzájemné zápasy", "vzajemne-zapasy")
        if (orderedTeams.isEmpty()) {
            Text("Zatím žádné týmy.")
        } else {
            Div(attrs = { classes("cross-table-wrap") }) {
                Table(attrs = { classes("cross-table") }) {
                    Tr {
                        Th { Text("Tým") }
                        orderedTeams.forEach { t ->
                            val fullName = t.teamNameOverride ?: t.team?.name ?: ""
                            Th(attrs = { attr("title", fullName) }) { Text(crossTableAbbrevById[t.teamId] ?: "") }
                        }
                    }
                    orderedTeams.forEach { rowTeam ->
                        Tr {
                            Th { Text(rowTeam.teamNameOverride ?: rowTeam.team?.name ?: "") }
                            orderedTeams.forEach { colTeam ->
                                if (rowTeam.teamId == colTeam.teamId) {
                                    Td(attrs = { classes("self") }) { Text("—") }
                                } else {
                                    val direct = matchByPair[rowTeam.teamId to colTeam.teamId]
                                        ?.takeIf { it.status == "completed" }
                                    val reverse = matchByPair[colTeam.teamId to rowTeam.teamId]
                                        ?.takeIf { it.status == "completed" }
                                    if (direct == null && reverse == null) {
                                        Td(attrs = { classes("planned") }) { Text("") }
                                    } else {
                                        val directFirst = when {
                                            direct == null -> false
                                            reverse == null -> true
                                            else -> roundOrderKey(direct) <= roundOrderKey(reverse)
                                        }
                                        val directBlock: @Composable () -> Unit = {
                                            direct?.let { m ->
                                                val home = m.homeDuelPoints ?: 0
                                                val away = m.awayDuelPoints ?: 0
                                                val cls = if (home > away) "win" else if (home < away) "loss" else "draw"
                                                Div(attrs = {
                                                    classes(cls)
                                                    onClick { selectedMatch = m }
                                                }) { Text("$home:$away") }
                                            }
                                        }
                                        val reverseBlock: @Composable () -> Unit = {
                                            reverse?.let { m ->
                                                val home = m.homeDuelPoints ?: 0
                                                val away = m.awayDuelPoints ?: 0
                                                // reverse match has colTeam as home; mirror to rowTeam:colTeam order
                                                val cls = if (away > home) "win" else if (away < home) "loss" else "draw"
                                                Div(attrs = {
                                                    classes(cls)
                                                    onClick { selectedMatch = m }
                                                }) { Text("$away:$home") }
                                            }
                                        }
                                        val playoffMatch = playoffMatchByPair[setOf(rowTeam.teamId, colTeam.teamId)]
                                        val playoffBlock: @Composable () -> Unit = {
                                            playoffMatch?.let { m ->
                                                val rowIsHome = m.homeTeamId == rowTeam.teamId
                                                val rowPts = if (rowIsHome) m.homeDuelPoints ?: 0 else m.awayDuelPoints ?: 0
                                                val colPts = if (rowIsHome) m.awayDuelPoints ?: 0 else m.homeDuelPoints ?: 0
                                                val cls = if (rowPts > colPts) "win" else if (rowPts < colPts) "loss" else "draw"
                                                Div(attrs = {
                                                    classes(cls)
                                                    attr("title", "Nadstavba")
                                                    onClick { selectedMatch = m }
                                                }) { Text("PO: $rowPts:$colPts") }
                                            }
                                        }
                                        Td {
                                            Div(attrs = { classes("cross-cell") }) {
                                                if (directFirst) {
                                                    directBlock()
                                                    reverseBlock()
                                                } else {
                                                    reverseBlock()
                                                    directBlock()
                                                }
                                                playoffBlock()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    Div(attrs = { classes("card"); attr("id", "zebricek-hracu") }) {
        SectionHeading("Žebříček jednotlivců — $leagueName", "zebricek-hracu")
        PlayerRankingTable(playerStats)
    }

    Div(attrs = { classes("card"); attr("id", "hrac-kola") }) {
        SectionHeading("Hráč kola", "hrac-kola")
        PlayerOfRoundSection(leagueId = leagueId, matches = matches)
    }

    val autumnMatches = remember(matches) { matches.filter { it.phase == "autumn" } }
    val springMatches = remember(matches) { matches.filter { it.phase == "spring" } }
    if (autumnMatches.isNotEmpty() && autumnMatches.all { it.status == "completed" }) {
        PhaseFinalStandings(leagueId, "autumn", autumnMatches, "Finální tabulka — podzim", "finale-podzim")
    }
    if (springMatches.isNotEmpty() && springMatches.all { it.status == "completed" }) {
        PhaseFinalStandings(leagueId, "spring", springMatches, "Finální tabulka — jaro", "finale-jaro")
    }

    // Nadstavba (spec 12/14): "O titul"/"O udržení" are their own standalone mini-table -- just
    // that group's own extra-round matches/points (fetchStandings(phase=playoff_*) computes it that
    // way server-side, see standings/index.ts), not the season's cumulative total the main "Tabulka
    // ligy" above already shows. Shown only once that group's own nadstavba round(s) are fully
    // played; before that, the group split is only visible as a badge in the main table (see
    // playoffGroupByTeam). No medal colors here -- 1./2./3. in a standalone extra-round mini-table
    // isn't "the podium" the way it is in the season/phase tables.
    val titlePlayoffMatches = remember(matches) { matches.filter { it.phase == "playoff_title" } }
    val relegationPlayoffMatches = remember(matches) { matches.filter { it.phase == "playoff_relegation" } }
    if (titlePlayoffMatches.isNotEmpty() && titlePlayoffMatches.all { it.status == "completed" }) {
        PhaseFinalStandings(leagueId, "playoff_title", titlePlayoffMatches, "Nadstavba — O titul", "nadstavba-o-titul", showMedals = false)
    }
    if (relegationPlayoffMatches.isNotEmpty() && relegationPlayoffMatches.all { it.status == "completed" }) {
        PhaseFinalStandings(leagueId, "playoff_relegation", relegationPlayoffMatches, "Nadstavba — O udržení", "nadstavba-o-udrzeni", showMedals = false)
    }

    selectedMatch?.let { match ->
        MatchDetailModal(
            matchId = match.id,
            homeDuelPoints = match.homeDuelPoints,
            awayDuelPoints = match.awayDuelPoints,
            homeTeamName = teamNameById[match.homeTeamId] ?: match.homeTeamId,
            awayTeamName = teamNameById[match.awayTeamId] ?: match.awayTeamId,
            leagueName = leagueName,
            onClose = { selectedMatch = null }
        )
    }
}
